import React, { useEffect, useState } from 'react';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { api } from '../../lib/supabase';
import { formatLocalDate } from '../../types';

interface UpcomingProspect {
  id: string;
  first_name: string;
  last_name: string;
  title: string;
  company_email: string | null;
  expected_start_date: string;
  days_until_start: number;
  status: string;
  created_by_first_name: string | null;
  created_by_last_name: string | null;
}

const UpcomingProspects: React.FC = () => {
  const [prospects, setProspects] = useState<UpcomingProspect[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUpcomingProspects();
  }, []);

  const loadUpcomingProspects = async () => {
    try {
      const data = await api.getUpcomingProspects();
      setProspects(data || []);
    } catch (err) {
      // Error handled
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-3xl border border-amber-200 bg-amber-50 p-6">
        <div className="text-amber-600 text-sm">Loading upcoming prospects...</div>
      </div>
    );
  }

  if (prospects.length === 0) {
    return null; // Don't show widget if no upcoming prospects
  }

  const getDaysLabel = (days: number) => {
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    return `In ${days} days`;
  };

  const getDaysColor = (days: number) => {
    if (days <= 1) return 'bg-red-100 text-red-700';
    if (days <= 3) return 'bg-amber-100 text-amber-700';
    return 'bg-slate-100 text-slate-600';
  };

  return (
    <div className="rounded-3xl border border-amber-200 bg-amber-50 p-6">
      <h3 className="text-lg font-semibold text-amber-900 flex items-center gap-2">
        <CalendarIcon className="h-5 w-5" />
        Upcoming Prospects ({prospects.length})
      </h3>
      <p className="text-sm text-amber-700 mt-1">Starting within the next 7 days</p>

      <div className="mt-4 space-y-3">
        {prospects.map((prospect) => (
          <div key={prospect.id} className="bg-white rounded-xl p-4 border border-amber-100">
            <div className="flex justify-between items-start">
              <div>
                <p className="font-semibold text-slate-900">
                  {prospect.first_name} {prospect.last_name}
                </p>
                <p className="text-sm text-slate-500">{prospect.title}</p>
                {prospect.company_email && (
                  <p className="text-sm text-slate-400">{prospect.company_email}</p>
                )}
                {prospect.created_by_first_name && (
                  <p className="text-xs text-slate-400 mt-1">
                    Recruited by: {prospect.created_by_first_name} {prospect.created_by_last_name}
                  </p>
                )}
              </div>
              <div className="text-right">
                <span
                  className={`px-2 py-1 rounded-full text-xs font-semibold ${getDaysColor(prospect.days_until_start)}`}
                >
                  {getDaysLabel(prospect.days_until_start)}
                </span>
                <p className="text-xs text-slate-400 mt-1">
                  {formatLocalDate(prospect.expected_start_date, { month: 'short', day: 'numeric' })}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UpcomingProspects;
